/**
 * Portal Auth event list — the pure half of the slide-out panel behind each
 * clickable stat on the clinic's Portal Auth tab.
 *
 * TEMPORARY MIGRATION TOOLING - TO BE REMOVED AFTER CUTOVER.
 */

/**
 * odin's patient page. It takes the patient `_id` and finds the clinic itself
 * (`BrandAutoSwitch`), so no clinic id is needed — the same link shape
 * `NewCustomersPage.jsx` already uses.
 */
export const ODIN_BASE_URL = 'https://core-odin.5axis.health'

export function odinPatientUrl(customerId) {
  if (typeof customerId !== 'string' || !/^[a-f0-9]{24}$/i.test(customerId)) return null
  return `${ODIN_BASE_URL}/dashboard/customers/${customerId}`
}

/** `?surface=none` on the API — a `password/login` event, which records no surface. */
export const NO_SURFACE = 'none'

/**
 * Event types only `firebase/migrate` writes. They carry no `meta.surface` —
 * the type already says where they came from — so the path is read off the
 * type, or every one of them would read "password login".
 */
const FIREBASE_MIGRATE_TYPES = new Set(['migration_attempted', 'migration_failed', 'password_migrated'])

/**
 * `meta.surface` in words. `null` is `password/login`, which does not set one,
 * unless `type` is one only the Firebase path writes.
 */
export function surfaceLabel(surface, type) {
  if ((surface === null || surface === undefined) && FIREBASE_MIGRATE_TYPES.has(type)) return 'firebase login'
  if (surface === null || surface === undefined || surface === NO_SURFACE) return 'password login'
  if (surface === 'firebase_migrate') return 'firebase login'
  return surface.replace(/_/g, ' ')
}

/** `meta.reason` as shown: the core constant, which is what an engineer greps for. */
export function reasonLabel(reason) {
  return reason === null || reason === undefined ? 'unspecified' : reason
}

/** A row's `details` as `key: value` pairs, for one compact cell. */
export function detailPairs(details) {
  return Object.entries(details || {}).map(([key, value]) => `${key.replace(/_/g, ' ')}: ${value}`)
}

/** Range presets the panel offers. 14 days is the server's hard window. */
export const RANGE_PRESETS = Object.freeze([
  { key: '24h', label: 'Last 24h', hours: 24 },
  { key: '7d', label: 'Last 7 days', hours: 7 * 24 },
  { key: '14d', label: 'Last 14 days', hours: 14 * 24 },
])

export const DEFAULT_RANGE = '7d'

/**
 * Query params for one page of the event list.
 *
 * `stat` is what was clicked (`type`, plus `purpose` for "never sent");
 * `filters` is what the operator narrowed it to in the panel.
 */
export function buildEventsParams(stat, filters = {}, { now = new Date(), cursor } = {}) {
  const preset = RANGE_PRESETS.find((entry) => entry.key === filters.range) ||
    RANGE_PRESETS.find((entry) => entry.key === DEFAULT_RANGE)
  const params = {
    type: stat.type,
    from: new Date(now.getTime() - preset.hours * 60 * 60 * 1000).toISOString(),
  }
  if (stat.purpose) params.purpose = stat.purpose
  if (filters.reason) params.reason = filters.reason
  if (filters.surface !== undefined && filters.surface !== '') {
    params.surface = filters.surface === null ? NO_SURFACE : filters.surface
  }
  if (cursor) params.cursor = cursor
  return params
}

/**
 * The surface breakdown is only worth a filter when the type is emitted from
 * more than one sign-in path — `login_failed` today. Elsewhere every row
 * carries the same surface, or none, and the strip would be noise.
 */
export function showsSurfaceFilter(surfaces) {
  return Array.isArray(surfaces) && surfaces.length > 1
}
